<template>
	<article class="article-container">
		<template v-if="this.loading">
			<div class="loading-curtain stack-vertical" data-justify="center" data-align="center">
				<base-icon name="loader"/>
			</div>
		</template>
		<template v-else>
			<header class="stack-vertical" :style="`background: var(--color-${article.colore})`">
				<router-link class="go-back" :to="{name: 'home'}" replace>
					{{$t('generic_indietro').toUpperCase()}}
				</router-link>
				<h1>{{article.titolo}}</h1>
				<div class="info">
					<a :href="$route.fullPath" rel="bookmark" class="article-permalink">
						<base-icon name="link 1" />
					</a>
					<div class="spacer"></div>
					<div class="data stack-vertical">
						<label for="pub-date" class="font-caption-maiusc-2">pubblicato</label>
						<time id="pub-date" datetime="{{article.publishedAt.split('T')[0]}}">
							{{this.$d(article.publishedAt, 'default')}}
						</time>
					</div>
					<div class="data stack-vertical">
						<label for="pub-author" class="font-caption-maiusc-2">scritto da</label>
						<address id="pub-author" rel="author">
							{{article.autore}}
						</address>
					</div>
				</div>
			</header>
			<main>
				<section
					class="section--text first"
					v-html="utils.markdownToHtml(article.primo_paragrafo)"
				/>
				<template v-for="content in article.contenuti">
					<section
						class="section--text"
						v-if="content.__component == 'contenuto-testuale.paragrafo'"
						v-html="utils.markdownToHtml(content.testo)"
					/>
					<section
						class="section--gallery"
						v-if="content.__component == 'contenuto-multimediale.media'"
					>
						<template v-for="media in content.media.data">
							<img :src="getImageURL(media)" v-if="media.attributes.mime.startsWith('image')" />
							<video controls :src="utils.parseMediaUrl(media.attributes.url)" v-if="media.attributes.mime.startsWith('video')" />
						</template>
						<div
							v-if="content.didascalia"
							v-html="utils.markdownToHtml(content.didascalia)"
							class="section--caption"
						/>
					</section>
				</template>
			</main>
			<section class="related-articles">
			</section>
			<section class="related-scrollies">
			</section>
		</template>
	</article>
</template>

<script type="text/javascript">
import { mapGetters, mapState, mapActions } from 'vuex';
import API from '@js/API.js';
import utils from '@js/utils.js';
import cloneDeep from 'lodash.clonedeep';

export default {
	name: 'Article',
	data() {
		return {
			utils,
			article: null,
			loading: true,
		};
	},
	computed: {
		...mapGetters(['isMobile']),
		...mapState(['scrollys']),
	},
	async mounted() {
		await this.initStore();
		await this.getArticle(this.$route.params.slug);
		this.loading = false;
	},
	methods: {
		...mapActions([ 'initStore' ]),
		async getArticle(link) {
			let result = await API.getArticles({
				filters: [{type: 'link', value: link}],
				populate: ['contenuti.media'],
			});
			result = await result.json();
			if (!result.data?.length) {
				this.$router.replace({ name: 'home' });
				return;
			}

			this.article = result.data[0].attributes;
		},
		getImageURL(media) {
			if (!media) return;

			const f = media.attributes.formats;
			if (f && utils.areAvifSupported()) {
				return utils.urlToPicture(f, this.isMobile ? ['medium', 'small', 'x-small'] : ['large', 'medium', 'small'])
			}

			return utils.parseMediaUrl(media.attributes.url); 
		},
		placeHolderItem() {
			return require('@assets/icons/placeholder_multi.svg');
		},
	},
}
</script>

<style lang="scss">
@import '@css/variables.scss';

body.article {
	overflow-y: auto;
}

.article-container {
	margin-left: var(--menu-size);

	@media (max-width: $breakpoint-sm) {
		margin-left: 0;
		margin-top: var(--menu-size);
	}

	header {
		color: var(--color-black-text);

		padding: 3rem 7.5rem;
		box-sizing: content-box;
		gap: 3rem;

		@media (max-width: $breakpoint-ml) {
			padding: 3rem 3rem;
			gap: 3rem;
		}

		@media (max-width: $breakpoint-sm) {
			padding: 1.5rem 1.25rem;
			gap: 1.5rem;
		}

		.go-back {
			font-family: 'Arzachel';
			font-size: 0.95rem;
			text-decoration: underline;
		}

		h1 {
			font-size: calc(3.5 * var(--base-font-size));
			line-height: 120%;
		}

		.article-permalink {
			display: none;

			padding: 0.75rem;
			background: var(--color-white);
			border-radius: 50%;

			align-self: flex-start;

			.svg-icon {
				width: 1.25rem;
				height: 1.25rem;
			}
		}

		.spacer {
			flex-grow: 1;
		}

		.info {
			display: flex;
			position: relative;

			.data {
				width: 15rem;
			}

			address {
				font-style: normal;
			}
		}

		@media (max-width: $breakpoint-ml) {
			.info {
				justify-content: space-between;
			}
		}

		@media (max-width: $breakpoint-sm) {
			h1 {
				font-size: calc(2.75 * var(--base-font-size));
			}

			.info {
				flex-direction: column-reverse;
				gap: 1rem;

				.spacer {
					display: none;
				}
			}
		}
	}

	main {
		display: flex;
		flex-direction: column;
		padding: 3rem 7.5rem 7.5rem;
		gap: 2rem;
		align-items: center;

		@media (max-width: $breakpoint-ml) {
			padding: 3rem;
			// gap: 3rem;
		}

		@media (max-width: $breakpoint-sm) {
			padding: 1.5rem 1.25rem 8rem;
			// gap: 3rem;
		}

		section {
			max-width: 49rem;
			width: 100%;
			margin: auto;
		}

		.section--text {
			line-height: 160%;
			color: var(--color-black-text);

			a {
				text-decoration: underline;
			}

			&.first {
				font-family: Laica;
				font-size: calc(1.5 * var(--base-font-size));
				line-height: 130%;
				margin-bottom: 2rem;

				@media (max-width: $breakpoint-sm) {
					margin-bottom: 0;
				}
			}
		}

		.section--gallery {
			display: flex;
			flex-direction: column;
			margin-top: 2rem;
			margin-bottom: 2rem;
			gap: 1rem;

			@media (max-width: $breakpoint-sm) {
				margin-top: 0;
				margin-bottom: 0;
			}

			video, img {
				max-height: 85vh; 
				width: 100%;
				object-fit: contain;
				margin: auto;
			}

			.section--caption {
				color: var(--color-gray-text);
				font-size: calc(1 * var(--base-font-size));
				line-height: 160%;

				a {
					text-decoration: underline;
				}

				@media (max-width: $breakpoint-sm) {
					font-size: calc(0.875 * var(--base-font-size));
				}
			}
		}
	}
}

.loading-curtain {
	position: fixed;
	z-index: 5;
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;
	background-color: var(--color-brand-blue);

	.svg-icon {
		--svg-icon-color: var(--color-white);
		height: 10rem;
		width: 10rem;

		animation-duration: 0.82s;
		animation-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1);
		animation-iteration-count: infinite;
		animation-direction: alternate-reverse;
		animation-name: arrow-bounce;
	}
}

@keyframes arrow-bounce {
	100% {transform: translateY(-15%);}
	0%   {transform: translateY(-9);}
}
</style>
